/**
 * Trim a string for case-request save payloads. Returns `null` when empty so the
 * field is always present in JSON and the backend can clear previously saved data.
 */
import { nullifyUndefinedDeep } from './nullifyPayload';
import type { SaveStepFields } from '../types/savePayloadFields';

export function strOrNull(v: string | undefined | null): string | null {
  const t = (v ?? '').trim();
  return t ? t : null;
}

/** When a step object is present on GET, map null/undefined to empty string instead of keeping prev. */
export function apiStepString(
  stepPresent: boolean,
  value: string | null | undefined,
  prev: string,
): string {
  if (!stepPresent) return prev;
  if (value == null) return '';
  return value.trim();
}

/** Existing draft/update id only — omit entirely for brand-new requests. */
export function caseRequestIdForSave(draftRequestId: string | undefined | null): string | undefined {
  const t = (draftRequestId ?? '').trim();
  return t ? t : undefined;
}

/** Normalize API `current_step` (1-based wizard step saved with a draft). */
export function parseCurrentStep(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const step = Math.trunc(value);
  return step >= 1 ? step : undefined;
}

/** Attach `current_step` to draft save payloads when the wizard step is known. */
export function applyDraftCurrentStep<T extends { current_step?: number }>(
  payload: T,
  options: { isDraft: boolean; currentStep?: number },
): void {
  if (!options.isDraft || options.currentStep == null) return;
  const step = parseCurrentStep(options.currentStep);
  if (step !== undefined) payload.current_step = step;
}

/** Finalize a case-request save payload: set draft step, then nullify undefined fields. */
export function finalizeCaseRequestSavePayload<T extends { current_step?: number }>(
  payload: T,
  options: { isDraft: boolean; currentStep?: number },
): T {
  applyDraftCurrentStep(payload, options);
  return nullifyUndefinedDeep(payload);
}

/** Prefer API `current_step` when continuing a saved draft; otherwise use legacy heuristics. */
export function draftResumeStepFromApi<T extends number>(
  response: { current_step?: unknown },
  fallback: () => T,
  isValidStep: (step: number) => boolean,
): T {
  const step = parseCurrentStep(response.current_step);
  if (step !== undefined && isValidStep(step)) return step as T;
  return fallback();
}

/** Widen optional string fields so save mappers can send explicit null to clear data. */
export type { SaveStepFields };